import React, { useState, useEffect } from "react";
import { Stack, TextInput, Textarea } from "@mantine/core";
import { LookupDictionary, LookupDictionaryInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";
import { ConnectorSelector } from "@/components/features/Connectors/ConnectorSelector";
import { CONNECTOR_CATEGORIES } from "@/config/connectorCategories";
import { useApp } from "@/contexts";
import { FEATURE_CODES } from "@/config";

export interface LookupDictionaryEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Lookup dictionary to edit (if null/undefined, it's create mode) */
  lookupDictionary?: LookupDictionary | null;

  /** Callback when lookup dictionary should be created */
  onCreateLookupDictionary?: (
    data: LookupDictionaryInput
  ) => Promise<LookupDictionary | null>;

  /** Callback when lookup dictionary should be updated */
  onUpdateLookupDictionary?: (
    id: string,
    data: LookupDictionaryInput
  ) => Promise<LookupDictionary | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormData {
  name: string;
  description: string;
  connectorId: string | null;
  status: number;
}

const initialFormData: FormData = {
  name: "",
  description: "",
  connectorId: null,
  status: 100,
};

export const LookupDictionaryEditModal: React.FC<
  LookupDictionaryEditModalProps
> = ({
  opened,
  onClose,
  lookupDictionary,
  onCreateLookupDictionary,
  onUpdateLookupDictionary,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const { chechAppPlanFeature } = useApp();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(lookupDictionary?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or lookupDictionary changes
  useEffect(() => {
    if (opened) {
      if (lookupDictionary) {
        // Edit mode - populate form with data
        setFormData({
          name: lookupDictionary.name || "",
          description: lookupDictionary.description || "",
          connectorId: lookupDictionary.connectorId || null,
          status: lookupDictionary.status ?? 100,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, lookupDictionary, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name is required
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle connector selection
  const handleConnectorChange = (connectorId: string | null) => {
    handleFieldChange("connectorId", connectorId);
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: LookupDictionaryInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        connectorId: formData.connectorId || null,
        status: formData.status,
      };

      let result: LookupDictionary | null = null;

      if (isEditMode && lookupDictionary?.id && onUpdateLookupDictionary) {
        result = await onUpdateLookupDictionary(
          lookupDictionary.id,
          submitData
        );
      } else if (!isEditMode && onCreateLookupDictionary) {
        result = await onCreateLookupDictionary(submitData);
      }

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Lookup dictionary submission error:", error);
    }
  };

  const supportsExternalDictionary = chechAppPlanFeature(
    FEATURE_CODES.EXTERNAL_LOOKUP_DICTIONARIES,
    true
  ).didSucceed();

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit Lookup Dictionary" : "Add Lookup Dictionary"}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Enter dictionary name"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          error={validationErrors.name}
          required
          withAsterisk
          size="md"
          description="A unique name to identify this lookup dictionary"
        />

        <Textarea
          label="Description"
          placeholder="Enter description (optional)"
          value={formData.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          description="A note to help describe the purpose of this dictionary"
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />

        <ConnectorSelector
          label="External Connector"
          description="Optional webhook connector for external lookups. Leave empty for local dictionary."
          placeholder="Select a webhook connector"
          value={formData.connectorId}
          onChange={handleConnectorChange}
          category={CONNECTOR_CATEGORIES.WEBHOOKS}
          size="md"
          premiumFeatureEnabled={supportsExternalDictionary}
        />

        <StatusSelector
          label="Status"
          value={formData.status}
          onChange={(value) => handleFieldChange("status", value)}
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};
